import { getTheme } from "@clipboard-health/ui-theme";
import { Box, Container, ThemeProvider } from "@mui/material";
import { useLegacyStateContext } from "@src/appV2/LegacyStateBridge/context";
import { useIsKeyboardOpen } from "@src/appV2/lib/utils/capacitor/useIsKeyboardOpen";
import { AnimatedBackgroundPageWrapper } from "@src/appV2/redesign/components/AnimatedBackgroundPageWrapper";
import { type ReactElement, useState } from "react";
import { Redirect, useLocation } from "react-router-dom";

import { AuthFooter } from "../components/Footer";
import { AuthHeadline } from "../components/Headline";
import { LoginSignUpForm } from "./Form/Form";
import { useLoginSignUpUiConfiguration } from "./hooks/useLoginSignUpUiConfiguration";

const fadeUp = {
  "@keyframes fadeUp": {
    from: { opacity: 0, transform: "translateY(12px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
};

export function LoginSignUpPage(): ReactElement {
  const { session } = useLegacyStateContext();
  const location = useLocation<{ from?: Location }>();
  const { hideFooterAndSecondaryButtonsOnKeyboardOpen } = useLoginSignUpUiConfiguration();
  const isKeyboardOpen = useIsKeyboardOpen();
  const [showAppVersionInfo, setShowAppVersionInfo] = useState(false);

  const shouldShowFooterAndSecondaryButtons = hideFooterAndSecondaryButtonsOnKeyboardOpen
    ? !isKeyboardOpen
    : true;

  if (session.isAuthorized) {
    const redirectPath = location.state?.from?.pathname ?? "/home";
    const redirectSearch = location.state?.from?.search ?? "";
    return <Redirect to={`${redirectPath}${redirectSearch}`} />;
  }

  return (
    <ThemeProvider theme={getTheme()}>
      <AnimatedBackgroundPageWrapper>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minHeight: "100%",
            paddingY: 4,
            paddingX: 6,
            position: "relative",
            zIndex: 3,
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              marginTop: 6,
            }}
          >
            <Container
              maxWidth="lg"
              sx={{
                ...fadeUp,
                animation: "fadeUp 700ms ease-out",
                display: "flex",
                flexDirection: "column",
                flex: 1,
              }}
            >
              <AuthHeadline
                onLogoSecretTap={() => {
                  setShowAppVersionInfo(true);
                }}
              />

              <Container
                maxWidth="md"
                sx={{
                  marginTop: 6,
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                }}
              >
                <LoginSignUpForm />
              </Container>
            </Container>
          </Box>
          {shouldShowFooterAndSecondaryButtons && (
            <AuthFooter showAppVersionInfo={showAppVersionInfo} />
          )}
        </Box>
      </AnimatedBackgroundPageWrapper>
    </ThemeProvider>
  );
}
